import styles from './EmailPreviewTemplate.module.css';
import {
  FiArrowLeft,
  FiDownload,
  FiTrash2,
  FiMail,
  FiMoreVertical,
  FiStar,
  FiCornerUpLeft,
  FiChevronDown,
} from 'react-icons/fi';
import { FaLinkedinIn, FaInstagram, FaFacebookF, FaYoutube } from 'react-icons/fa';
import { ReactComponent as SkillGigs } from '../../../assets/img/SkillGigs_tm-black 4.svg';
import { ReactComponent as AppStoreBadge } from '../../../assets/img/app-store-badge.svg';
import { ReactComponent as GooglePlayBadge } from '../../../assets/img/google-play-badge.svg';


const SUMMARY_ICONS = {
  'Title':      'https://skillgigs-v3-stag-documents-cl.s3.us-east-2.amazonaws.com/content/templates-assets/Vector.png',
  'Job Type':   'https://skillgigs-v3-stag-documents-cl.s3.us-east-2.amazonaws.com/content/templates-assets/Vector (1).png',
  'Start Date': 'https://skillgigs-v3-stag-documents-cl.s3.us-east-2.amazonaws.com/content/templates-assets/Vector (2).png',
  'Location':   'https://skillgigs-v3-stag-documents-cl.s3.us-east-2.amazonaws.com/content/templates-assets/Vector (3).png',
  'Skills':     'https://skillgigs-v3-stag-documents-cl.s3.us-east-2.amazonaws.com/content/templates-assets/Vector (4).png',
};

const buildTemplate3Summary = (selectedJob) => {
  if (!selectedJob) return [];
  return [
    { label: 'Title', value: selectedJob.title },
    { label: 'Job Type', value: selectedJob.type },
    { label: 'Start Date', value: selectedJob.startDate },
    { label: 'Location', value: selectedJob.location },
    ...(selectedJob.skillsList?.length
      ? [{ label: 'Skills', chips: selectedJob.skillsList }]
      : []),
  ].filter((d) => d.value || d.chips?.length);
};

const renderTemplate3Summary = (details) => {
  if (!details.length) return <span>Job Detail</span>;
  return (
    <div className={styles.jobSummaryList}>
      {details.map((detail) => (
        <div key={detail.label} className={styles.jobSummaryItem}>
          <div className={styles.jobSummaryItemHeader}>
            {SUMMARY_ICONS[detail.label] && (
              <img
                src={SUMMARY_ICONS[detail.label]}
                alt=""
                className={styles.jobSummaryIcon}
              />
            )}
            <span className={styles.jobSummaryItemLabel}>{detail.label}</span>
          </div>
          {detail.chips ? (
            <div className={styles.skillChipsRow}>
              {detail.chips.map((chip) => (
                <span key={chip} className={styles.skillChip}>{chip}</span>
              ))}
            </div>
          ) : (
            <span className={styles.jobSummaryItemValue}>{detail.value}</span>
          )}
        </div>
      ))}
    </div>
  );
};

const buildTemplateConfig = (companyName, employerName, jobTitle) => {
  const co = companyName || 'Company Name';
  const em = employerName || companyName || 'Employer Name';
  const jt = jobTitle || 'Job Title';
  return {
    'Template 1': {
      subject: `You have a job invitation from ${co}`,
      title: `You have a job invitation from ${co}`,
      greatNews: 'Great news! Your profile stood out.',
      intro: `${em} at ${co} reviewed your profile and invited you to apply.`,
      messageSectionLabel: 'Message from the Hiring Team',
      hideEmployerName: true,
      jobSectionLabel: 'Job Details',
      buttonText: 'Accept Invite',
      buttonSmall: false,
      paraBeforeBtn: null,
      showDownloadPara: true,
      supportEmail: 'support@skillgigs.com',
    },
    'Template 2': {
      subject: `Your job invitation for ${jt} is waiting`,
      title: `Your job invitation for ${jt} is waiting`,
      intro: `${co} invited you because they believe you're a strong match for the role ${jt}.`,
      messageSectionLabel: 'Message from the Hiring Team',
      hideEmployerName: true,
      jobSectionLabel: 'Job Details',
      buttonText: 'Submit A Bid',
      buttonSmall: false,
      paraBeforeBtn: null,
      showDownloadPara: true,
      supportEmail: 'support@skillgigs.com',
    },
    'Template 3': {
      subject: `${em} wants to hire you for ${jt}`,
      title: `${em} wants to hire you for ${jt}`,
      intro: `Your background matched an open role and ${em} wants you to apply.`,
      messageSectionLabel: 'Message from the Hiring Team',
      hideEmployerName: true,
      jobSectionLabel: 'Job Details',
      buttonText: 'See Pay Rate & Apply',
      buttonSmall: true,
      paraBeforeBtn: null,
      showDownloadPara: true,
      supportEmail: 'support@skillgigs.com',
    },
  };
};

const EmailPreviewTemplate = ({ talentFirstName, jobTitle, selectedJob, message, selectedTemplate = 'Template 1', companyName, employerName }) => {
  const TEMPLATE_CONFIG = buildTemplateConfig(companyName, employerName, jobTitle)
  const config = TEMPLATE_CONFIG[selectedTemplate] || TEMPLATE_CONFIG['Template 1']
  const displayedJobDetails = buildTemplate3Summary(selectedJob);

  return (
    <div className={styles.container}>
      {/* Top navigation bar */}
      <div className={styles.topNav}>
        <FiArrowLeft className={styles.navIcon} />
        <div className={styles.navIcons}>
          <FiDownload className={styles.navIcon} />
          <FiTrash2 className={styles.navIcon} />
          <FiMail className={styles.navIcon} />
          <FiMoreVertical className={styles.navIcon} />
        </div>
      </div>

      {/* Email subject line */}
      <div className={styles.subjectRow}>
        <div className={styles.subjectContent}>
          <span className={styles.subjectText}>
            {config.subject}
          </span>
          <span className={styles.inboxBadge}>Inbox</span>
        </div>
        <FiStar className={styles.starIcon} />
      </div>

      {/* Sender information */}
      <div className={styles.senderRow}>
        <div className={styles.senderAvatar}>SG</div>
        <div className={styles.senderInfo}>
          <div>
            <span className={styles.senderName}>Skillgigs</span>
            <span className={styles.senderTime}>Yesterday</span>
          </div>
          <div className={styles.toMe}>
            to me <FiChevronDown size={14} />
          </div>
        </div>
        <div className={styles.senderActions}>
          <FiCornerUpLeft className={styles.actionIcon} />
          <FiMoreVertical className={styles.actionIcon} />
        </div>
      </div>

      {/* Email body */}
      <div className={styles.emailBody}>
        <div className={styles.sgLogoContainer}>
          <SkillGigs />
        </div>

        <p className={styles.emailPara}>
          Hi {talentFirstName || 'Candidate First Name'},
        </p>

        {config.greatNews && <p className={styles.emailPara}>{config.greatNews}</p>}

        <p className={styles.emailPara}>{config.intro}</p>

        <>
          {config.hideEmployerName ? (
            <p className={styles.sectionLabel}>{config.messageSectionLabel}</p>
          ) : (
            <>
              <p className={styles.sectionLabel}>{employerName || companyName || 'Employer Name'}</p>
              <p className={styles.employerCompany}>{companyName || 'Employer Company'}</p>
            </>
          )}
          <div className={styles.messageBox}>
            &ldquo;{message || 'Employer Custom Message'}&rdquo;
          </div>
          <p className={styles.sectionLabel}>{config.jobSectionLabel}</p>
          {renderTemplate3Summary(displayedJobDetails)}
        </>

        {config.paraBeforeBtn && <p className={styles.emailPara}>{config.paraBeforeBtn}</p>}

        <a
          href="https://skillgigs.com"
          className={`${styles.bidButton}${config.buttonSmall ? ` ${styles.bidButtonSmall}` : ''}`}
          target="_blank"
          rel="noreferrer"
        >
          {config.buttonText}
        </a>

        {config.showDownloadPara && (
          <p className={styles.emailPara}>
            Never Miss a Job Opportunity. Download Now on{' '}
            <a href="https://apps.apple.com/app/skillgigs/id1476711597" className={styles.contactLink} target="_blank" rel="noreferrer">iOS</a> or{' '}
            <a href="https://play.google.com/store/apps/details?id=com.skillgigs" className={styles.contactLink} target="_blank" rel="noreferrer">Android</a>.
          </p>
        )}

        <p className={styles.thanksText}>
          Thanks,<br />
          <strong>The SkillGigs Team</strong>
        </p>

        <p className={styles.contactText}>
          If you believe this is an error or need help, reply to this email or reach us at{' '}
          <a href="tel:8447545544" className={styles.contactLink}>(844) 754-5544</a> or at{' '}
          <a href={`mailto:${config.supportEmail || 'info@skillgigs.com'}`} className={styles.contactLink}>{config.supportEmail || 'info@skillgigs.com'}</a>.
        </p>

        <p className={styles.unsubscribeText}>
          If you would prefer not to receive job invitations or emails from SkillGigs, you can{' '}
          <a href="https://skillgigs.com/unsubscribe" className={styles.unsubscribeLink} target="_blank" rel="noreferrer">
            unsubscribe here
          </a>.
        </p>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerLogo}>
          <SkillGigs className={styles.footerLogoSvg} />
        </div>

        <div className={styles.socialIcons}>
          <a href="https://www.linkedin.com/company/skillgigs" target="_blank" rel="noreferrer" className={styles.socialIcon}>
            <FaLinkedinIn />
          </a>
          <a href="https://www.instagram.com/skillgigs" target="_blank" rel="noreferrer" className={styles.socialIcon}>
            <FaInstagram />
          </a>
          <a href="https://www.facebook.com/skillgigs" target="_blank" rel="noreferrer" className={styles.socialIcon}>
            <FaFacebookF />
          </a>
          <a href="https://www.youtube.com/@skillgigs" target="_blank" rel="noreferrer" className={styles.socialIcon}>
            <FaYoutube />
          </a>
        </div>

        <div className={styles.footerAddress}>
          5177 Richmond Ave, Ste 600 Houston, TX 77056
        </div>

        <div className={styles.footerBadges}>
          <div className={styles.badgeRow}>
            <a href="https://apps.apple.com/app/skillgigs/id1476711597" target="_blank" rel="noreferrer" className={styles.appBadge}>
              <AppStoreBadge />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.skillgigs" target="_blank" rel="noreferrer" className={styles.appBadge}>
              <GooglePlayBadge />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPreviewTemplate;
